import { createAsyncThunk } from '@reduxjs/toolkit';

import { fetchWrapper } from '../../../../helpers';
import { TREATMENT_PLANS_V2_API_BASE } from '../../constants';

import type { IChildBackground, IChildSchedule } from '../../../../pages/common';
import type {
  ITreatmentPlanV3ChildPage,
  GetTreatmentPlanV3ChildPagePayload,
  UpdateTreatmentPlanV3ChildInfoPayload,
  CreateTreatmentPlanV3ChildBackgroundPayload,
  DeleteTreatmentPlanV3ChildBackgroundPayload,
  CreateTreatmentPlanV3ChildSchedulePayload,
  DeleteTreatmentPlanV3ChildSchedulePayload,
  TreatmentPlanV3ChildPageThunkConfig,
} from './types';

export function createTreatmentPlanV3ChildThunks() {
  return {
    getTreatmentPlanV3ChildPage: getTreatmentPlanV3ChildPage(),
    updateTreatmentPlanV3ChildInfo: updateTreatmentPlanV3ChildInfo(),
    createTreatmentPlanV3ChildBackground: createTreatmentPlanV3ChildBackground(),
    deleteTreatmentPlanV3ChildBackground: deleteTreatmentPlanV3ChildBackground(),
    createTreatmentPlanV3ChildSchedule: createTreatmentPlanV3ChildSchedule(),
    deleteTreatmentPlanV3ChildSchedule: deleteTreatmentPlanV3ChildSchedule(),
  };
}

function shouldFetchChildPage(
  { treatmentPlanId }: GetTreatmentPlanV3ChildPagePayload,
  { getState }: { getState: () => TreatmentPlanV3ChildPageThunkConfig['state'] },
) {
  const { childPage, childPageLoading, childPageTreatmentPlanId } = getState().treatmentPlanV3;
  if (childPageTreatmentPlanId !== treatmentPlanId) return true;
  return childPage == null && !childPageLoading;
}

function getTreatmentPlanV3ChildPage() {
  return createAsyncThunk<
    ITreatmentPlanV3ChildPage,
    GetTreatmentPlanV3ChildPagePayload,
    TreatmentPlanV3ChildPageThunkConfig
  >(
    'treatmentPlanV3/getTreatmentPlanV3ChildPage',
    async ({ treatmentPlanId, getAccessTokenSilently }) =>
      fetchWrapper.get({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/child/`,
        getAccessTokenSilently,
      }),
    { condition: shouldFetchChildPage },
  );
}

function updateTreatmentPlanV3ChildInfo() {
  return createAsyncThunk<ITreatmentPlanV3ChildPage, UpdateTreatmentPlanV3ChildInfoPayload>(
    'treatmentPlanV3/updateTreatmentPlanV3ChildInfo',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.patch(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/child/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function createTreatmentPlanV3ChildBackground() {
  return createAsyncThunk<IChildBackground, CreateTreatmentPlanV3ChildBackgroundPayload>(
    'treatmentPlanV3/createTreatmentPlanV3ChildBackground',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/child/backgrounds/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3ChildBackground() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3ChildBackgroundPayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3ChildBackground',
    async ({ treatmentPlanId, backgroundId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/child/backgrounds/${backgroundId}/`,
        getAccessTokenSilently,
      });
    },
  );
}

function createTreatmentPlanV3ChildSchedule() {
  return createAsyncThunk<IChildSchedule, CreateTreatmentPlanV3ChildSchedulePayload>(
    'treatmentPlanV3/createTreatmentPlanV3ChildSchedule',
    async ({ treatmentPlanId, data, getAccessTokenSilently }) =>
      fetchWrapper.post(
        {
          url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/child/schedule/`,
          getAccessTokenSilently,
        },
        data,
      ),
  );
}

function deleteTreatmentPlanV3ChildSchedule() {
  return createAsyncThunk<void, DeleteTreatmentPlanV3ChildSchedulePayload>(
    'treatmentPlanV3/deleteTreatmentPlanV3ChildSchedule',
    async ({ treatmentPlanId, scheduleId, getAccessTokenSilently }) => {
      await fetchWrapper.delete({
        url: `${TREATMENT_PLANS_V2_API_BASE}/${treatmentPlanId}/pages/child/schedule/${scheduleId}/`,
        getAccessTokenSilently,
      });
    },
  );
}
